import React, { useState } from 'react'
import styled from 'styled-components'
import theme from '../styles/theme'

const TabsContainer = styled.div`
  background: ${theme.color.background.frame};
  border-radius: 12px;
  box-shadow: 0 8px 16px ${theme.color.shadow.medium}, 
              0 4px 8px ${theme.color.shadow.light};
  margin-bottom: 2rem;
  overflow: hidden;
`

const TabsHeader = styled.div`
  display: flex;
  background: ${theme.color.background.light};
  border-bottom: 2px solid ${theme.color.border.light};
  box-shadow: inset 0 -2px 4px ${theme.color.shadow.light};
`

const Tab = styled.button<{ active: boolean }>`
  flex: 1;
  padding: 1.2rem 1.5rem;
  border: none;
  background: ${props => props.active ? theme.color.background.frame : 'transparent'};
  color: ${props => props.active ? theme.color.text.base : theme.color.text.grey};
  font-size: 1.3rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 0;
  position: relative;
  
  ${props => props.active && `
    box-shadow: inset 0 2px 4px ${theme.color.shadow.light};
    transform: translateY(2px);
  `}
  
  &:hover:not([disabled]) {
    background: ${props => props.active ? theme.color.background.frame : theme.color.background.lightGrey};
  }
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${props => props.active ? theme.color.accent.primary : 'transparent'};
    transition: background 0.2s ease;
  }
`

const NotificationBadge = styled.span`
  position: absolute;
  top: 8px;
  right: 8px;
  background: #dc3545;
  color: white;
  border-radius: 10px;
  padding: 2px 6px;
  font-size: 1.1rem;
  font-weight: 700;
  min-width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  box-shadow: 0 2px 4px rgba(220, 53, 69, 0.3);
`

const TabContent = styled.div`
  padding: 2rem;
  background: ${theme.color.background.frame};
`

interface TabItem {
  id: string
  label: string
  content: React.ReactNode
  badge?: number
}

interface TabContainerProps {
  tabs: TabItem[]
  defaultTab?: string
}

const TabContainer: React.FC<TabContainerProps> = ({ tabs, defaultTab }) => {
  const [activeTab, setActiveTab] = useState(defaultTab || tabs[0]?.id)

  return (
    <TabsContainer>
      <TabsHeader>
        {tabs.map(tab => (
          <Tab
            key={tab.id}
            active={activeTab === tab.id}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
            {tab.badge !== undefined && tab.badge > 0 && (
              <NotificationBadge>{tab.badge}</NotificationBadge>
            )}
          </Tab>
        ))}
      </TabsHeader>
      {tabs.map(tab => (
        <TabContent key={tab.id} style={{ display: activeTab === tab.id ? 'block' : 'none' }}>
          {tab.content}
        </TabContent>
      ))}
    </TabsContainer>
  )
}

export default TabContainer